import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type {
  AbsenceBalance,
  AbsenceRequestSummary,
  AbsenceType,
  HalfDayPart,
} from './types';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AbsenceRequestModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();

  const types = useQuery({
    queryKey: ['absences', 'types'],
    queryFn: () => api<AbsenceType[]>('/absences/types'),
    enabled: open,
  });

  const balances = useQuery({
    queryKey: ['absences', 'balance'],
    queryFn: () => api<AbsenceBalance[]>('/absences/balance'),
    enabled: open,
  });

  const [absenceTypeId, setAbsenceTypeId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPart, setHalfDayPart] = useState<HalfDayPart>('AM');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isJustified, setIsJustified] = useState(false);
  const [comment, setComment] = useState('');

  const selectedType = useMemo(
    () => types.data?.find((t) => t.id === absenceTypeId) ?? null,
    [types.data, absenceTypeId],
  );

  // Las horas justificadas cambian el formulario: en vez de un rango de días
  // se pide un tramo horario de un único día.
  const isHourly = selectedType?.code === 'JUSTIFIED_HOURS';

  const balanceForType = useMemo(
    () => balances.data?.find((b) => b.absenceTypeId === absenceTypeId) ?? null,
    [balances.data, absenceTypeId],
  );

  const hoursRequested = useMemo(() => {
    if (!isHourly || !startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    return Math.max(0, eh * 60 + em - (sh * 60 + sm)) / 60;
  }, [isHourly, startTime, endTime]);

  const previewDays = useMemo(
    () => estimateBusinessDays(startDate, endDate, isHalfDay),
    [startDate, endDate, isHalfDay],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      api<AbsenceRequestSummary>('/absences', {
        method: 'POST',
        body: JSON.stringify({
          absenceTypeId,
          startDate,
          endDate: isHourly ? startDate : endDate || startDate,
          isHalfDay: !isHourly && isHalfDay && (!endDate || endDate === startDate),
          halfDayPart: !isHourly && isHalfDay ? halfDayPart : undefined,
          startTime: isHourly ? startTime : undefined,
          endTime: isHourly ? endTime : undefined,
          isJustified: isHourly ? isJustified : undefined,
          comment: comment.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      onClose();
      reset();
    },
  });

  if (!open) return null;

  function reset() {
    setAbsenceTypeId('');
    setStartDate('');
    setEndDate('');
    setIsHalfDay(false);
    setHalfDayPart('AM');
    setStartTime('');
    setEndTime('');
    setIsJustified(false);
    setComment('');
    createMutation.reset();
  }

  function handleClose() {
    if (createMutation.isPending) return;
    reset();
    onClose();
  }

  const sameDay = !endDate || endDate === startDate;
  const hourlyOk = !isHourly || (startTime !== '' && endTime !== '' && endTime > startTime);
  const canSubmit =
    absenceTypeId !== '' && startDate !== '' && hourlyOk && !createMutation.isPending;

  const error = createMutation.error
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Error al enviar la solicitud'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={handleClose}
        className="absolute inset-0"
        style={{
          background: 'rgba(10,14,31,0.4)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[520px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">Solicitar ausencia</h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="abs-type">
                Tipo de ausencia <span className="text-destructive">*</span>
              </Label>
              <select
                id="abs-type"
                value={absenceTypeId}
                onChange={(e) => setAbsenceTypeId(e.target.value)}
                required
                className="h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">— Selecciona un tipo —</option>
                {(types.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="abs-from">
                  Desde <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="abs-from"
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              {isHourly ? null : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="abs-to">Hasta</Label>
                  <Input
                    id="abs-to"
                    type="date"
                    min={startDate || undefined}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <span className="text-[11px] text-muted-foreground">
                    Vacío = mismo día que "Desde"
                  </span>
                </div>
              )}
            </div>

            {isHourly ? (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="abs-start-time">
                      Desde las <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="abs-start-time"
                      type="time"
                      required
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="abs-end-time">
                      Hasta las <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="abs-end-time"
                      type="time"
                      required
                      min={startTime || undefined}
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>

                {startTime && endTime && endTime <= startTime ? (
                  <p className="text-[12px] text-destructive">
                    La hora de fin debe ser posterior a la de inicio.
                  </p>
                ) : hoursRequested > 0 ? (
                  <p className="text-[12px] text-muted-foreground">
                    Solicitas <b className="text-foreground">{formatHours(hoursRequested)}</b> del
                    día.
                  </p>
                ) : null}

                <div className="flex flex-col gap-1.5">
                  <label className="inline-flex items-start gap-2 text-sm font-medium text-foreground">
                    <input
                      type="checkbox"
                      checked={isJustified}
                      onChange={(e) => setIsJustified(e.target.checked)}
                      className="h-4 w-4 mt-0.5 rounded-xs border border-input accent-icaria-600"
                    />
                    Entrego justificante (médico o similar)
                  </label>
                  <p className="text-[11px] text-muted-foreground pl-6">
                    {isJustified
                      ? 'Si RR. HH. da por bueno el justificante, estas horas no gastan vacaciones.'
                      : 'Sin justificante, estas horas se descuentan de tu saldo de vacaciones.'}
                  </p>
                </div>
              </div>
            ) : null}

            {!isHourly && sameDay && startDate ? (
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={isHalfDay}
                    onChange={(e) => setIsHalfDay(e.target.checked)}
                    className="h-4 w-4 rounded-xs border border-input accent-icaria-600"
                  />
                  Medio día
                </label>
                {isHalfDay ? (
                  <div className="flex items-center gap-4 text-sm pl-6">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="half"
                        checked={halfDayPart === 'AM'}
                        onChange={() => setHalfDayPart('AM')}
                        className="h-3.5 w-3.5 accent-icaria-600"
                      />
                      Mañana
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="half"
                        checked={halfDayPart === 'PM'}
                        onChange={() => setHalfDayPart('PM')}
                        className="h-3.5 w-3.5 accent-icaria-600"
                      />
                      Tarde
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="abs-comment">Motivo (opcional)</Label>
              <Textarea
                id="abs-comment"
                rows={3}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            {selectedType && balanceForType && balanceForType.consumesBalance ? (
              <BalanceInfo
                typeName={selectedType.name}
                available={balanceForType.availableDays}
                requested={previewDays}
              />
            ) : null}

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
              {createMutation.isPending ? 'Enviando…' : 'Enviar solicitud'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function BalanceInfo({
  typeName,
  available,
  requested,
}: {
  typeName: string;
  available: number;
  requested: number;
}) {
  const exceeds = requested > available;
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 px-4 py-2.5 rounded-sm border',
        exceeds
          ? 'bg-destructive/5 border-destructive/40'
          : 'bg-icaria-100 border-icaria-200',
      )}
    >
      <span
        className={cn(
          'text-[13px] font-medium',
          exceeds ? 'text-destructive' : 'text-icaria-700',
        )}
      >
        Saldo de {typeName.toLowerCase()}
      </span>
      <span
        className={cn(
          'font-mono text-[13px] font-semibold tabular-nums',
          exceeds ? 'text-destructive' : 'text-icaria-700',
        )}
      >
        {available} días{requested > 0 ? ` · usarías ${formatDays(requested)}` : ''}
      </span>
    </div>
  );
}

function formatDays(d: number): string {
  if (Number.isInteger(d)) return String(d);
  return d.toFixed(1).replace('.', ',');
}

/**
 * Estima días laborables (lun-vie) entre dos fechas, sólo para previsualización.
 * El backend recalcula el número definitivo respetando festivos del calendario.
 */
function estimateBusinessDays(start: string, end: string, halfDay: boolean): number {
  if (!start) return 0;
  const s = new Date(start);
  if (Number.isNaN(s.getTime())) return 0;
  const e = end ? new Date(end) : new Date(start);
  if (Number.isNaN(e.getTime())) return 0;

  if (halfDay && (!end || end === start)) return 0.5;

  let count = 0;
  const cur = new Date(s);
  cur.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  while (cur.getTime() <= e.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/** "1h 30m" a partir de un número de horas decimal. */
function formatHours(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
